import { axiosInstance } from "@/lib/api";

/**
 * Uploads the operator's existing strategy PDF and returns its documentId, to be
 * handed to POST /social-strategies/:brandId as `importedDocumentId`.
 *
 * Same three-step brand-attachment flow as the chat rail (presign -> PUT -> process),
 * with one difference: no sessionId. The upload happens BEFORE the strategy exists,
 * so there is no chat to attach it to — it hangs off the brand and the strategy row
 * points at it. Uploading first is also what makes the failure mode clean: a failed
 * upload means no half-made strategy, because nothing was created yet.
 *
 * The returned document is in "processing" state; the analyser makes it searchable
 * a little later. The agent is told about that state rather than being left to
 * guess, so an import in flight never reads as an empty file.
 */

type UploadUrlResponse = {
  documentId: string;
  uploadUrl: string;
  uploadToken: string;
};

export const IMPORT_MAX_BYTES = 25 * 1024 * 1024;

/** Matches the server's allow-list: the attachment pipeline is PDF-only. */
export function isImportableStrategyFile(file: File): boolean {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

export async function uploadStrategySource(
  brandId: string,
  file: File,
): Promise<string> {
  const { data } = await axiosInstance.post<UploadUrlResponse>(
    `/brand/${brandId}/attachments/upload-url`,
    {
      filename: file.name,
      mimetype: "application/pdf",
      extension: "pdf",
    },
  );

  const put = await fetch(data.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": "application/pdf" },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  await axiosInstance.post(`/brand/${brandId}/attachments/process`, {
    uploadToken: data.uploadToken,
  });

  return data.documentId;
}
